import {
  type QueryClient,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getChatMessages } from "api-client";
import { observer } from "mobx-react-lite";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { assert, CHAT_QUERY_KEYS, WS_EVENT_TYPES } from "utils";

import { useAuthStore } from "src/auth";
import {
  useEventSubscription,
  useOnWSReconnect,
  useReconnectCount,
} from "src/events";
import { myChatsInfiniteQueryOptions } from "src/queries/chats";

import { ChatStore } from "./ChatStore";

interface ChatContextValue {
  store: ChatStore;
}

const ChatContext = createContext<ChatContextValue | null>(null);

function invalidateMyChats(queryClient: QueryClient): void {
  queryClient
    .invalidateQueries({
      queryKey: [CHAT_QUERY_KEYS.ROOT, CHAT_QUERY_KEYS.MY_CHATS],
    })
    .catch(function (err) {
      console.warn("[Chat] invalidate chats failed:", err);
    });
}

function invalidateChatCaches(queryClient: QueryClient, chatId?: string): void {
  invalidateMyChats(queryClient);
  const messagesKey =
    chatId === undefined
      ? [CHAT_QUERY_KEYS.ROOT, CHAT_QUERY_KEYS.MESSAGES]
      : [CHAT_QUERY_KEYS.ROOT, CHAT_QUERY_KEYS.MESSAGES, chatId];
  queryClient
    .invalidateQueries({ queryKey: messagesKey })
    .catch(function (err) {
      console.warn("[Chat] invalidate messages failed:", err);
    });
}

export const ChatContextProvider = observer(function ChatContextProvider(
  props: PropsWithChildren,
) {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  const isAuthenticated = authStore.isAuthenticated;

  const [store] = useState(() => {
    return new ChatStore();
  });

  useEffect(
    function cleanupStoreOnLogout() {
      if (!isAuthenticated) {
        store.cleanup();
        queryClient.removeQueries({ queryKey: [CHAT_QUERY_KEYS.ROOT] });
      }
    },
    [isAuthenticated, store, queryClient],
  );

  useInfiniteQuery({
    ...myChatsInfiniteQueryOptions(store, ""),
    enabled: isAuthenticated,
  });

  useOnWSReconnect(() => {
    if (!isAuthenticated) {
      return;
    }

    invalidateChatCaches(queryClient);
  });

  useEventSubscription(WS_EVENT_TYPES.MESSAGE, (event) => {
    if (!isAuthenticated) {
      return;
    }

    const message = {
      id: event.payload.message.id,
      content: event.payload.message.content,
      createdAt: event.payload.message.createdAt,
      sender: event.payload.sender,
      wasSentBySameUser: event.createdById === authStore.user.id,
      chatId: event.payload.chatId,
      attachments: event.payload.message.attachments ?? [],
      isDeleted: false,
      editedAt: null,
    };

    store.addIncomingMessage(message);

    if (!store.chatsById[event.payload.chatId]) {
      invalidateMyChats(queryClient);
    }
  });

  useEventSubscription(WS_EVENT_TYPES.MESSAGE_DELETED, (event) => {
    if (!isAuthenticated) {
      return;
    }

    store.markMessageDeleted(event.payload.chatId, event.payload.messageId);
    invalidateChatCaches(queryClient, event.payload.chatId);
  });

  useEventSubscription(WS_EVENT_TYPES.MESSAGE_EDITED, (event) => {
    if (!isAuthenticated) {
      return;
    }

    store.applyMessageEdit({
      chatId: event.payload.chatId,
      messageId: event.payload.message.id,
      content: event.payload.message.content,
      editedAt: event.payload.message.editedAt,
    });
    invalidateChatCaches(queryClient, event.payload.chatId);
  });

  useEventSubscription(WS_EVENT_TYPES.CHAT_READ, (event) => {
    if (!isAuthenticated) {
      return;
    }

    store.applyChatRead(event.payload.chatId, event.payload.readAt);
  });

  useEventSubscription(WS_EVENT_TYPES.CHAT_UNREAD, (event) => {
    if (!isAuthenticated) {
      return;
    }

    store.applyChatUnread(event.payload.chatId, event.payload.readAt);
  });

  const value = useMemo(() => {
    return { store };
  }, [store]);

  return (
    <ChatContext.Provider value={value}>{props.children}</ChatContext.Provider>
  );
});

function useChatContext() {
  const chatContext = useContext(ChatContext);
  assert(
    chatContext !== null,
    "useChatContext must be used within ChatContextProvider",
  );
  return chatContext;
}

export function useChatStore() {
  const value = useChatContext();
  return value.store;
}

export function useRefetchMyChats() {
  const queryClient = useQueryClient();
  return function refetchMyChats() {
    invalidateMyChats(queryClient);
  };
}

export function useLoadMessagesForChat(chatId: string) {
  const store = useChatStore();
  const reconnectCount = useReconnectCount();

  const messagesQuery = useInfiniteQuery({
    queryKey: [
      CHAT_QUERY_KEYS.ROOT,
      CHAT_QUERY_KEYS.MESSAGES,
      chatId,
      reconnectCount,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await getChatMessages({
        path: { chatId },
        query: { cursor: pageParam, limit: 50 },
      });
      const payload = response.data.data;
      store.setMessages(chatId, payload.messages);
      return payload;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return messagesQuery;
}
